// pages/Payment.js
import React, { useEffect, useRef, useState, lazy, Suspense, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Calendar,
  ArrowLeft,
  CheckCircle2,
  Edit,
  XCircle,
  FileText,
  AlertTriangle,
  Info,
  Trash,
  DollarSign,
  CreditCard,
  User,
  Clock,
} from "lucide-react";
import Breadcrumbs from "../../components/private/Breadcrumbs.jsx";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  usePermissions,
  useUser,
  useUserRoles,
} from "../../authentication/authentication";
import {
  apiFetchError,
  apiRequest,
  apiSuccess,
} from "../../redux/api/apiActions";
import errorChecker from "../../utils/errorChecker";
import { useDispatch } from "react-redux";
import api from "../../api";
import formatDate from "../../utils/formatDate";
import SubmitLoading from "../../components/common/SubmitLoading";

// Lazy-load payment components
const PaymentOverview = lazy(() =>
  import("../../components/private/payments/payment/view/PaymentOverview.jsx")
);
const EditablePayment = lazy(() =>
  import("../../components/private/payments/payment/edite/EditablePayment.jsx")
);

import { extractNewData } from "../../utils/deepCompare.ts";
import { clearConfirmationAlerts, hideAlert, showAlert } from "../../redux/alert/alertActions.js";
import EditWarningMessage from "../../components/common/EditWarningMessage.jsx";
import VisitHistory from "../../components/private/payments/payment/view/VisitHistory.jsx";
import Invoice from "../../components/private/payments/payment/view/Invoice.jsx";
import ViewPageSkeleton from "../../components/skeletons/ViewPageSkeleton.jsx";
import formatTime from "../../utils/formatTime.js";
import Txt from "../../components/Text";
import { arClass } from "../../utils/useArClass";
import textTranslator from "../../utils/useTextTranslator";
import { isAdminRole } from "../../utils/adminChecker.js";


const Text = ({ children, className, page = "PAYMENTS" }) => {
  return (
    <Txt page={page} className={className} keyName={page}>
      {children}
    </Txt>
  );
};

const paymentTabs = ["Overview", "Invoice", "Visit History"];

// Custom hook for theme
const useTheme = () => {
  const darkMode = useSelector((state) => state.settings.darkMode);

  return {
    darkMode,
    colors: {
      bg: {
        primary: darkMode ? "bg-gray-950" : "bg-gray-50",
        secondary: darkMode ? "bg-gray-900" : "bg-white",
        card: darkMode ? "bg-gray-900/50" : "bg-white",
        hover: darkMode ? "hover:bg-gray-800/80" : "hover:bg-gray-50",
      },
      text: {
        primary: darkMode ? "text-gray-100" : "text-gray-900",
        secondary: darkMode ? "text-gray-400" : "text-gray-600",
        muted: darkMode ? "text-gray-500" : "text-gray-500",
      },
      border: {
        primary: darkMode ? "border-gray-800" : "border-gray-200",
        secondary: darkMode ? "border-gray-700" : "border-gray-300",
      },
      status: {
        paid: darkMode
          ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
          : "bg-emerald-100 text-emerald-800 border-emerald-200",
        pending: darkMode
          ? "bg-amber-500/10 text-amber-400 border-amber-500/20"
          : "bg-amber-100 text-amber-800 border-amber-200",
        failed: darkMode
          ? "bg-rose-500/10 text-rose-400 border-rose-500/20"
          : "bg-rose-100 text-rose-800 border-rose-200",
        refunded: darkMode
          ? "bg-indigo-500/10 text-indigo-400 border-indigo-500/20"
          : "bg-indigo-100 text-indigo-800 border-indigo-200",
        "partially-paid": darkMode
          ? "bg-yellow-500/10 text-yellow-400 border-yellow-500/20"
          : "bg-yellow-100 text-yellow-800 border-yellow-200",
        cancelled: darkMode
          ? "bg-red-500/10 text-red-400 border-red-500/20"
          : "bg-red-100 text-red-800 border-red-200",
        "on-hold": darkMode
          ? "bg-gray-500/10 text-gray-400 border-gray-500/20"
          : "bg-gray-100 text-gray-800 border-gray-200",
      },
      accent: {
        primary: darkMode
          ? "bg-gradient-to-r from-green-600 to-emerald-600"
          : "bg-gradient-to-r from-green-500 to-emerald-500",
        hover: darkMode
          ? "hover:from-green-700 hover:to-emerald-700"
          : "hover:from-green-600 hover:to-emerald-600",
        text: "text-white",
      },
    },
  };
};

// Enhanced Tab Button with Active Indicator
const TabButton = ({ active, onClick, children, count, disabled }) => {
  const theme = useTheme();
  if (!children) {
    return null;
  }
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className={arClass(`
        relative px-6 py-3 rounded-xl font-medium transition-all duration-300 max-sm:text-xs max-sm:px-2
        flex items-center gap-3 text-sm overflow-hidden disabled:opacity-70 disabled:cursor-not-allowed
        ${
          active
            ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg shadow-green-500/25"
            : `${theme.colors.text.secondary} border ${theme.colors.border.primary} ${theme.colors.bg.hover}`
        }
      `)}
      disabled={disabled}
    >
      {active && (
        <motion.div
          layoutId="activeTab"
          className="absolute inset-0 bg-gradient-to-r from-green-600 to-emerald-600"
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        />
      )}
      <div className="relative z-10 flex items-center gap-3">
        <div className="flex flex-col">{children}</div>
        {count !== undefined && count > 0 && (
          <span
            className={`
            text-xs px-2 py-0.5 rounded-full min-w-[24px] text-center font-semibold
            ${
              active
                ? "bg-white/20 backdrop-blur-sm"
                : theme.darkMode
                ? "bg-gray-800 text-gray-300"
                : "bg-gray-200 text-gray-600"
            }
          `}
          >
            {count}
          </span>
        )}
      </div>
    </motion.button>
  );
};

/* ---------------- Main Payment Component ---------------- */
const Payment = () => {
  const darkMode = useSelector((state) => state.settings.darkMode);
  const acceptedPolicies = useSelector(
      (state) => state.acknowledgments.acceptedPolicies ?? []
    );
    const modificationsAcknowledged = acceptedPolicies.some(
      (p) => p.policyId === 2 && p.accepted === true
    );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const paymentKey = `payment_${id}`;
  const updatePaymentKey = `update_payment_${id}`;
  const deletePaymentKey = `delete_payment_${id}`;
  const generateInvoiceKey = `generate_payment_${id}`;
  const deleteInvoiceKey = `delete_invoice_${id}`;
  const { requests } = useSelector((state) => state.api);
  const paymentLoading = requests[paymentKey]?.loading;
  const updatedPayment = requests[updatePaymentKey]?.success;
  const generateInvoiceSuccess = requests[generateInvoiceKey]?.success;
  const deletedInvoiceSuccess = requests[deleteInvoiceKey]?.success;
  const paymentUpdateLoading = requests[updatePaymentKey]?.loading;
  const deletePaymentLoading = requests[deletePaymentKey]?.loading;
  const generateInvoiceLoading = requests[generateInvoiceKey]?.loading;
  const deleteInvoiceLoading = requests[deleteInvoiceKey]?.loading;
  const receiveData = useSelector((state) => state.realTimeData);
  const roles = useUserRoles();
  const user = useUser();
  const { permissions, loading: permissionsLoading } = usePermissions();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState("Overview");
  const [payment, setPayment] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedPayment, setEditedPayment] = useState(null);
  const [oldPayment, setOldPayment] = useState(null);
  const [isChanged, setIsChanged] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  useEffect(() => {
    if (payment) {
      setEditedPayment({ ...payment });
      setOldPayment(payment);
    }
  }, [payment]);

  const canEdit =
    isAdminRole(roles) ||
    (!isAdminRole(roles) && checkAccess("putBilling"));
  const canDelete =
    isAdminRole(roles) ||
    (!isAdminRole(roles) && checkAccess("deletePayment"));

  const handleSave = async () => {
    if (!editedPayment || !oldPayment) return;

    const result = extractNewData(oldPayment, editedPayment, {
      normalize: true,
      dateCompareMode: "date",
      includeModified: true,
      includeAdded: true,
    });

    const changes = result.combined || {};

    if ((result.changeCount || 0) === 0 || Object.keys(changes).length === 0) {
      return;
    }

    try {
      dispatch(apiRequest("PUT", updatePaymentKey));

      const updateData = {
        ...changes,
      };

      await api.put(`payments/payment-id/${id}`, updateData);
dispatch(
        apiSuccess(
          "PUT",
          updatePaymentKey,
          textTranslator("paymentUpdated", "PAYMENTS")
        )
      );
      setPayment({ ...editedPayment });
      setOldPayment({ ...editedPayment });
      setIsEditing(false);
      setIsChanged(false);

      dispatch(
        showAlert({
          type: "success",
          title: textTranslator("Success", "PAYMENTS"),
          message: textTranslator("paymentUpdated", "PAYMENTS"),
        })
      );
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error || "Failed to update payment";
      dispatch(apiFetchError("PUT", paymentKey, errorMessage));
      dispatch(
        showAlert({
          type: "error",
          title: "Error",
          message: errorMessage,
        })
      );
    }
  };

  const handleDelete = () => {
    dispatch(clearConfirmationAlerts());
    dispatch(
      showAlert({
        id: `delete-confirmation-${payment.id}`,
        type: "confirmation",
        title: textTranslator("Delete Payment", "PAYMENTS"),
        message: `${textTranslator("deletePaymentConfirm", "PAYMENTS")} #${payment.id}?`,
        onCancel: cancelDelete,
        onConfirm: () => confirmDelete(payment),
      })
    );
  };

  const confirmDelete = (payment) => {
    dispatch(apiRequest("DELETE", deletePaymentKey));
    api
      .delete(`payments/user-id/${user.id}`, {
        data: {
          selectedIds: [payment.id],
        },
      })
      .then((res) => {
        dispatch(
          apiSuccess(
            "DELETE",
            deletePaymentKey,
            textTranslator("paymentDeleted", "PAYMENTS")
          )
        );

        dispatch(
          showAlert({
            type: "success",
            title: textTranslator("Deleted", "PAYMENTS"),
            message: `Payment #${payment.id} ${textTranslator("paymentDeleted", "PAYMENTS")}`,
          })
        );
        navigate("/payments");
      })
      .catch((error) => {
        const errorMessage = error.response?.data?.error;
        dispatch(apiFetchError(errorMessage, "DELETE", deletePaymentKey));
      });
  };

  const cancelDelete = (id) => {
    dispatch(hideAlert(id));
  };

  const links = {
    authorizedLinks: [
      { label: textTranslator("Payments", "PAYMENTS"), url: "/payments" },
      { label: "Payment Details", url: `/payments/${id}` },
    ],
  };

  const fetchPayment = useCallback(async () => {
    if (role === "staff" && !permissions?.paymentsPermissions?.get) {
      navigate("/dashboard");
      return;
    }
    dispatch(apiRequest("GET", paymentKey));
    api
      .get(`payments/payment-id/${id}`)
      .then((res) => {
        setPayment(res.data.payment);
        dispatch(
          apiSuccess("GET", paymentKey, "Fetched payment successfully")
        );
      })
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.error || "Error fetching payment data.";
        const type = error?.response?.data?.type || null;
        errorChecker(errorMessage, type, dispatch, errorMessage);
      });
  }, [id]);

  useEffect(() => {
    fetchPayment();
  }, [fetchPayment]);

  useEffect(() => {
    if (updatedPayment || generateInvoiceSuccess || deletedInvoiceSuccess) {
      fetchPayment();
    }
  }, [updatedPayment, generateInvoiceSuccess, deletedInvoiceSuccess]);

  const [realTimeLoading, setRealTimeLoading] = useState(false);

  useEffect(() => {
    if (receiveData.dataModel === "payment" && receiveData.data && receiveData.methodType === "put") {
      setRealTimeLoading(true);
      const receivedPayment = receiveData.data;
      if (receivedPayment.id.toString() === id.toString()) {
        setPayment(receivedPayment);
        setEditedPayment(receivedPayment);
        setOldPayment(receivedPayment);
        setRealTimeLoading(false);
      }
    }
  }, [receiveData, id]);

  const handleChangeDetected = (value) => {
    setIsChanged(value);
  };

  const handleConfirmationChange = () => {
    setShowConfirmationModal(false);
  };

  const renderContent = () => {
    const contentProps = {
      payment: isEditing ? editedPayment : payment,
      theme,
      loading: paymentLoading,
    };

    switch (activeTab) {
      case "Overview":
        return isEditing ? (
          <EditablePayment
            {...contentProps}
            isEditing={isEditing}
            editedPayment={editedPayment}
            setEditedPayment={setEditedPayment}
            onChangeDetected={(value) => handleChangeDetected(value)}
            oldPayment={oldPayment}
          />
        ) : (
          <PaymentOverview {...contentProps} />
        );
      case "Invoice":
        if (isEditing) return null;
        return <Invoice {...contentProps} />;
      case "Visit History":
        if (isEditing) return null;
        return <VisitHistory {...contentProps} />;
      default:
        return null;
    }
  };

  const getTabCount = (tabName) => {
    if (paymentLoading || !payment) return undefined;

    switch (tabName) {
      case "Visit History":
        return payment.visit ? 1 : 0;
      default:
        return undefined;
    }
  };

  if ((paymentLoading && !payment) || realTimeLoading) {
    return (
      <div
        className={arClass(`min-h-screen transition-colors duration-200 ${theme.colors.bg.primary}`)}
      >
        <Breadcrumbs links={links.authorizedLinks} />
        <ViewPageSkeleton />
      </div>
    );
  }

  const colors = theme.colors;

  return (
    <div className={arClass(`min-h-screen transition-colors duration-200`)}>
      <Breadcrumbs links={links.authorizedLinks} />
      {(paymentUpdateLoading || 
        deletePaymentLoading || 
        generateInvoiceLoading || 
        deleteInvoiceLoading) && (
        <SubmitLoading
          colors={colors}
          message={
            deletePaymentLoading
              ? textTranslator("deletingPayment", "PAYMENTS")
              : paymentUpdateLoading
              ? textTranslator("Updating payment...", "PAYMENTS")
              : generateInvoiceLoading
              ? textTranslator("generateInvoice", "PAYMENTS")
              : deleteInvoiceLoading
              ? textTranslator("deletingInvoice", "PAYMENTS")
              : "Processing..."
          }
        />
      )}
      <div
        className={arClass(`flex flex-col w-full rounded-lg p-6 min-h-[calc(100vh-5rem)] max-sm:p-4 ${theme.colors.bg.primary}`)}
      >
        {/* Header */}
        <div className={arClass("mb-8 max-sm:mb-4")}>
          <motion.button
            onClick={() => navigate("/payments")}
            whileHover={{ x: -5 }}
            whileTap={{ scale: 0.95 }}
            className={arClass(`flex items-center gap-2 mb-6 px-4 py-2 rounded-xl transition-all
              ${
                theme.darkMode
                  ? "text-gray-300 hover:bg-gray-800 border border-gray-800"
                  : "text-gray-600 hover:bg-gray-100 border border-gray-200"
              }`)}
          >
            <ArrowLeft className="w-4 h-4" />
            <span className={arClass("font-medium max-sm:text-xs")}>
              <Text>Back to Payments</Text>
            </span>
          </motion.button>

           <div className={arClass("flex items-center justify-between max-lg:flex-col max-lg:items-start gap-4")}>
            <div>
              <h1
                className={arClass(`text-4xl max-sm:text-lg font-bold mb-2 ${theme.colors.text.primary}`)}
              >
                <Text>Payment</Text> #{payment?.id}
              </h1>
              <p
                className={arClass(`flex items-center gap-3 text-lg max-sm:text-xs ${theme.colors.text.secondary}`)}
              >
                <span className={arClass("flex items-center gap-2 max-sm:text-xs max-sm:flex-col")}>
                  <Calendar className="w-4 h-4" />
                  {formatDate(payment?.paymentDate, true)}
                </span>
                
                <span className={arClass(theme.colors.text.muted)}>•</span>
                <span className={arClass("flex items-center gap-2 max-sm:text-xs max-sm:flex-col")}>
                  <User className="w-4 h-4" />
                  {payment?.patient?.firstName}{" "}
                  {payment?.patient?.lastName}
                </span>
                <span className={arClass(theme.colors.text.muted)}>•</span>
                <span className={arClass("flex items-center gap-2 max-sm:text-xs max-sm:flex-col")}>
                  <DollarSign className="w-4 h-4" />
                  {payment?.amountAfterDiscount || payment?.amount}
                </span>
              </p>
            </div>

            <div className="flex items-center gap-3 max-sm:w-full max-[390px]:flex-col max-[390px]:gap-2">
              <div
                              className={arClass(`flex items-center justify-end mb-6 p-3 max-sm:p-2 max-sm:mb-4 max-sm:w-full max-sm:justify-center max-[390px]:flex-col max-[390px]:gap-2`)}
                            >
                {(canEdit || canDelete) && (
                                    <div className="flex items-center gap-2 max-sm:flex-col max-sm:w-full">

                    {!isEditing ? (
                      <div className="flex items-center gap-3 max-sm:w-full max-[390px]:flex-col max-[390px]:gap-2">
                        {
                          canEdit && (
                            <motion.button
                          onClick={() => {
                            setIsEditing(true);
                            setActiveTab("Overview");
                          }}
                          className={`flex items-center gap-2 px-4 py-2 rounded-md border transition-colors duration-200 max-sm:text-xs max-sm:px-2 min-w-max max-sm:w-full ${
                            theme.darkMode
                              ? "bg-yellow-800 !text-yellow-100 border-yellow-100"
                              : "bg-yellow-100 !text-yellow-800 border-yellow-800"
                          }`}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Edit className="w-4 h-4" />
                          <Text>Edit Payment</Text>
                        </motion.button>
                          )
                        }
                        {
                          canDelete && (<motion.button
                          onClick={handleDelete}
                          className={`flex items-center gap-2 px-4 py-2 rounded-md border transition-colors duration-200 max-sm:text-xs max-sm:px-2 min-w-max max-sm:w-full ${
                            theme.darkMode
                              ? "bg-rose-800 !text-rose-100 border-rose-100"
                              : "bg-rose-100 !text-rose-800 border-rose-800"
                          }`}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Trash className="w-4 h-4" />
                          <Text>deletePayment</Text>
                        </motion.button>
                          )
                        }
                      </div>
                    ) : (
                     <div className="flex items-center gap-3 max-sm:w-full max-[390px]:flex-col max-[390px]:gap-2">
                        {isEditing && !showConfirmationModal && (
                          <motion.button
                            onClick={() => setShowConfirmationModal(true)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-md border transition-colors duration-200 max-sm:text-xs max-sm:px-2 max-sm:w-full min-w-max ${
                              theme.darkMode
                                ? "bg-yellow-800 !text-yellow-100 border-yellow-100"
                                : "bg-yellow-100 !text-yellow-800 border-yellow-800"
                            }`}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <AlertTriangle className="w-4 h-4" />
                            <Text>Notice</Text>
                          </motion.button>
                        )}

                        <motion.button
                          onClick={handleSave}
                          disabled={!(isChanged && modificationsAcknowledged)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-md border transition-colors duration-200 disabled:opacity-50 max-sm:text-xs max-sm:px-2 max-sm:w-full min-w-max ${
                            theme.darkMode
                              ? "bg-green-800 !text-green-100 border-green-100"
                              : "bg-green-100 !text-green-800 border-green-800"
                          }`}
                          whileHover={{
                            scale:
                              isChanged && modificationsAcknowledged ? 1.03 : 1,
                          }}
                          whileTap={{
                            scale:
                              isChanged && modificationsAcknowledged ? 0.97 : 1,
                          }}
                          title={
                            !isChanged
                              ? "No changes detected"
                              : !modificationsAcknowledged
                              ? "Please confirm all sections before saving"
                              : "Save Changes"
                          }
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          <Text>Save Changes</Text>
                        </motion.button>

                        <motion.button
                          onClick={() => {
                            setIsEditing(false);
                            setEditedPayment({ ...payment });
                            setIsChanged(false);
                          }}
                          className={`flex items-center gap-2 px-4 py-2 rounded-md border transition-colors duration-200 max-sm:text-xs max-sm:px-2 max-sm:w-full min-w-max ${
                            theme.darkMode
                              ? "bg-red-800 !text-red-100 border-red-100"
                              : "bg-red-100 !text-red-800 border-red-800"
                          }`}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <XCircle className="w-4 h-4" />
                          <Text>Cancel</Text>
                        </motion.button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className={arClass("flex gap-3 mb-8 flex-wrap max-sm:mb-6")}>
          {paymentTabs.map((tab) => (
            <TabButton
              key={tab}
              active={activeTab === tab}
              onClick={() => setActiveTab(tab)}
              count={getTabCount(tab)}
              disabled={isEditing ? tab !== "Overview" : false}
            >
              <Text>{tab}</Text>
              <small>{isEditing && "Editing Mode"}</small>
            </TabButton>
          ))}
        </div>

        {/* Edit Warning and Action Buttons */}
        {isEditing && showConfirmationModal && (
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="my-4"
          >
            <EditWarningMessage
              theme={theme}
              onConfirm={handleConfirmationChange}
              onClose={() => setShowConfirmationModal(false)}
            />
          </motion.div>
        )}

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Payment;